import styled from 'styled-components';
import { Banner, useBannerContext } from '../context/banner-context';
import { BannerHelpers } from '../utils/use-banner-helpers';

const getContainerGridArea = (data: Banner, helpers: BannerHelpers) => {
	if (helpers.isHorizontal) {
		if (helpers.isSingleItem || helpers.isAllImagesEnabled) {
			return 'center';
		}

		return data.textPosition.toLowerCase();
	}
	return helpers.isSingleItem ? 'top' : 'bottom';
};

const getContainerJustifySelf = (data: Banner, helpers: BannerHelpers) => {
	if (helpers.isHorizontal && !helpers.isAllImagesEnabled) {
		return data.textPosition.toLowerCase();
	}
	return 'stretch';
};

const Container = styled.div<{
	gridArea: ReturnType<typeof getContainerGridArea>;
	justifySelf: ReturnType<typeof getContainerJustifySelf>;
	textAlignment: Banner['textAlign'];
	isDesktop: boolean;
}>`
	display: grid;
	grid-gap: ${({ isDesktop }) => (isDesktop ? '24px' : '12px')};
	grid-template-columns: 1fr;
	grid-template-rows: auto;
	grid-area: ${({ gridArea }) => gridArea};
	align-items: start;
	align-content: center;
	justify-self: ${({ justifySelf }) => justifySelf};
	width: 100%;
	text-align: ${({ textAlignment }) => textAlignment.toLowerCase()};
	z-index: 1;
`;

const Title = styled.div<{ isDesktop: boolean }>`
	color: rgba(var(--carouselTitleColor), 1);
	font: ${({ isDesktop }) =>
		isDesktop ? 'var(--multiLayerCarouselTitleFontLarge)' : 'var(--multiLayerCarouselTitleFont)'};
	text-transform: var(--multiLayerCarouselTitleTextTransform, none);
`;

const Description = styled.div<{ isDesktop: boolean }>`
	color: rgba(var(--carouselDescColor), 1);
	font: ${({ isDesktop }) =>
		isDesktop ? 'var(--multiLayerCarouselDescFontLarge)' : 'var(--multiLayerCarouselDescFont)'};
`;

const CtaButton = styled.button<{
	isDesktop: boolean;
	textAlignment: Banner['textAlign'];
}>`
	display: inline-block;
	min-width: ${({ isDesktop }) => (isDesktop ? '220px' : 'unset')};
	height: ${({ isDesktop }) => (isDesktop ? '52px' : '36px')};
	background-color: var(--multiLayerCarouselButtonBgColor, rgba(var(--primary), 1));
	color: rgba(var(--multiLayerCarouselButtonColor), 1);
	font: ${({ isDesktop }) =>
		isDesktop ? 'var(--multiLayerCarouselButtonFontLarge)' : 'var(--multiLayerCarouselButtonFont)'};
	border-radius: var(--multiLayerCarouselButton-border-radius, 6px);
	padding: ${({ isDesktop }) => (isDesktop ? '0 24px' : '0 12px')};
	align-self: end;
	text-transform: var(--brand-text-transform, none);
	justify-self: ${({ textAlignment }) => textAlignment.toLowerCase()};
	transition: background-color 0.25s ease, transform 0.3s ease;
	&:hover {
		background-color: var(--button-primary-bg-hover, rgba(var(--primary-light), 1));
		color: var(--button-primary-text-hover, rgba(var(--font-dark), 1));
	}
	&:active {
		transform: scale(0.85);
	}
`;

const TextGroup: React.FC = () => {
	const { data, helpers } = useBannerContext();

	if (helpers.isAnyTextVisible) {
		const containerGridArea = getContainerGridArea(data, helpers);
		const containerJustifySelf = getContainerJustifySelf(data, helpers);

		return (
			<Container
				gridArea={containerGridArea}
				justifySelf={containerJustifySelf}
				textAlignment={data.textAlign}
				isDesktop={helpers.isDesktop}
			>
				{data.headerText && (
					<Title isDesktop={helpers.isDesktop} data-at="carousel-title" className="carousel-title">
						{data.headerText}
					</Title>
				)}

				{data.descriptionText && (
					<Description
						isDesktop={helpers.isDesktop}
						data-at="carousel-description"
						className="carousel-description"
					>
						{data.descriptionText}
					</Description>
				)}

				{data.ctaText && (
					<CtaButton textAlignment={data.textAlign} isDesktop={helpers.isDesktop} data-at="carousel-button">
						{data.ctaText}
					</CtaButton>
				)}
			</Container>
		);
	}
	return null;
};

export default TextGroup;
